import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Book, Music, Users, Menu, X, Settings, Sun, Moon, HelpCircle, Coins, LogOut, User, Scale } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAdmin } from "../contexts/AdminContext";
import { useUser } from "../contexts/UserContext";
import { STUDIO_LOGO, LOGO_CDN } from "../lib/constants";
import AuthModal from "./AuthModal";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { isAdmin, logout: adminLogout } = useAdmin();
  const { user, logout: userLogout } = useUser();

  const publicLinks = [
    { path: "/", label: "Home", icon: Book },
    { path: "/episodes", label: "Episodes", icon: Book },
    { path: "/store", label: "Store", icon: Coins },
    { path: "/witness-stand", label: "Witness Stand", icon: Scale },
    { path: "/bible", label: "Series Bible", icon: Users },
  ];

  const navLinks = isAdmin
    ? [...publicLinks, { path: "/production", label: "Production", icon: Settings }]
    : publicLinks;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img
                src={STUDIO_LOGO}
                alt="Red Ink Studios"
                className="h-12 w-12 rounded-lg object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = LOGO_CDN; }}
                data-testid="nav-logo-img"
              />
              <div className="hidden sm:block">
                <span className="font-cinzel text-lg tracking-wider leading-none">RED INK</span>
                <span className="block text-[10px] tracking-[0.3em] text-red-500 font-mono">STUDIOS</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(({ path, label }) => (
                <Link key={path} to={path}
                  className={`nav-link uppercase text-sm tracking-widest ${location.pathname === path ? 'active' : ''}`}
                  data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}>
                  {label}
                </Link>
              ))}

              {/* Theme Toggle */}
              <button onClick={toggleTheme}
                className="p-2 hover:bg-white/10 rounded-full transition-colors" data-testid="theme-toggle"
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-blue-600" />}
              </button>

              <Link to="/help" className="p-2 hover:bg-white/10 rounded-full transition-colors" data-testid="nav-help-btn" title="Help & User Guide">
                <HelpCircle size={20} className="text-gray-500 hover:text-white" />
              </Link>

              {/* User Auth */}
              {user ? (
                <div className="flex items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-red-600/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-red-400">{user.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-xs text-gray-400 max-w-[80px] truncate hidden lg:inline">{user.name?.split(" ")[0]}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-mono" data-testid="user-credits">{user.credits || 0}c</span>
                  <button onClick={userLogout} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" data-testid="user-logout-btn" title="Sign out">
                    <LogOut size={14} className="text-gray-500" />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowAuth(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-all"
                  data-testid="nav-signin-btn">
                  <User size={14} /> Sign In
                </button>
              )}

              {/* Admin Logout */}
              {isAdmin && (
                <button onClick={adminLogout} className="p-2 hover:bg-red-500/20 rounded-full transition-colors" data-testid="admin-logout-btn" title="Admin sign out">
                  <LogOut size={18} className="text-red-400" />
                </button>
              )}
            </div>

            {/* Mobile */}
            <div className="flex items-center gap-2 md:hidden">
              {user ? (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-mono">{user.credits || 0}c</span>
                </div>
              ) : (
                <button onClick={() => setShowAuth(true)} className="p-2 text-red-400" data-testid="mobile-signin-btn">
                  <User size={20} />
                </button>
              )}
              <button onClick={toggleTheme} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-blue-600" />}
              </button>
              <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 stagger-children">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link key={path} to={path}
                  className={`flex items-center gap-3 py-3 ${location.pathname === path ? 'text-red-500' : 'text-gray-400'}`}
                  onClick={() => setMobileMenuOpen(false)}>
                  <Icon size={18} />
                  <span className="uppercase tracking-wider text-sm">{label}</span>
                </Link>
              ))}
              {user && (
                <button onClick={() => { userLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 py-3 text-gray-400">
                  <LogOut size={18} /><span className="uppercase tracking-wider text-sm">Sign Out</span>
                </button>
              )}
            </div>
          )}
        </div>
      </nav>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
};

export default Navigation;
